import { Component, OnInit, signal } from '@angular/core';
import { Button } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { NgForOf, NgIf } from '@angular/common';
import { PipeModule } from '@shared/pipes/pipe.module';
import { PrimeTemplate } from 'primeng/api';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { OutboundCall } from '@modules/calls/Domain/call.model';
import { OutboundCallService } from '@modules/calls/Application/outboundCall.service';
import { FilterContainerComponent } from '@shared/components/filters/Components/filter-container/filter-container.component';
import {
  AppliedFilter,
  BaseFilter,
  FilterCriteria,
} from '@shared/components/filters/Domain/filter.model';
import { OutboundCallFacade } from '@modules/calls/Application/outboundCall.facade';
import { SearchOrigin } from '@shared/components/filters/Domain/search-method.enum';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';
import { OperationFilterService } from '@modules/operations/services/operation-filter.service';
import { RouterLink } from '@angular/router';
import { ToastService } from '@services/ui';
import { CustomComponentsModule } from '@shared/components/custom-components.module';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { DialogModule } from 'primeng/dialog';
import { CallAudioService } from '@services/calls/call-audio.service';
import WaveSurfer from 'wavesurfer.js';
import { CallTranscriptionDialogComponent } from '@shared/components/call-transcription-dialog/call-transcription-dialog/call-transcription-dialog.component';
import { DividerModule } from 'primeng/divider';
import { OutboundCallAudioDialogComponent } from '@shared/components/outbound-call-audio-dialog/outbound-call-audio-dialog.component';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-outbound-call',
  standalone: true,
  imports: [
    Button,
    ChipModule,
    ConfirmPopupModule,
    NgIf,
    PipeModule,
    PrimeTemplate,
    SkeletonModule,
    TableModule,
    ToastModule,
    TooltipModule,
    TranslateModule,
    FilterContainerComponent,
    RouterLink,
    CustomComponentsModule,
    BadgeModule,
    AvatarModule,
    DialogModule,
    NgForOf,
    CallTranscriptionDialogComponent,
    DividerModule,
    OutboundCallAudioDialogComponent,
    TagModule,
  ],
  templateUrl: './outbound-call.component.html',
  styleUrl: './outbound-call.component.scss',
})
export class OutboundCallComponent implements OnInit {
  outboundCalls = signal<OutboundCall[]>([]);
  loading = signal<boolean>(false);
  cursor = signal<string | null>(null);
  hasMore = signal(true);
  currentPage = signal(1);
  allCalls = signal<OutboundCall[]>([]);
  playingCallId = signal<string | null>(null);
  totalResults = signal<number>(0);

  filters: BaseFilter[] = [];
  defaultSelectedFilters: AppliedFilter[] = [];
  isTranscriptionDialogVisible = false;
  selectedTranscription: { message: string; speaker: string }[] = [];
  selectedCustomerName = '';

  //Wave and sound
  currentTimeMap: Record<string, number> = {};
  durationMap: Record<string, number> = {};
  loadingAudioCalls: Record<string, boolean> = {};
  waveSurfer: WaveSurfer | null = null;
  isAudioDialogVisible = false;
  selectedCallAudio: OutboundCall | null = null;
  loadingDownloads: Record<string, boolean> = {};

  protected readonly SearchOrigin = SearchOrigin;

  constructor(
    private outboundCallService: OutboundCallService,
    private filterService: FilterStorageService,
    private operationFilters: OperationFilterService,
    private toast: ToastService,
    private callAudioService: CallAudioService,
    public facade: OutboundCallFacade,
  ) {}

  ngOnInit(): void {
    this.filters = this.facade.getFilters();

    const saved = this.filterService.load(this.facade.key);

    if (saved) {
      this.facade.lastCriteria.set(saved);
      this.defaultSelectedFilters = this.operationFilters.convertToAppliedFilters(
        saved,
        this.filters,
      );
      this.loadCall(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
    } else {
      this.loadCall(SearchOrigin.RESET_OR_RELOAD, this.facade.lastCriteria());
    }
  }

  loadCall(origin: SearchOrigin = SearchOrigin.FILTER_CHANGE, event?: FilterCriteria) {
    this.loading.set(true);

    const criteria = this.facade.resolveSearchCriteria(event, origin);

    if (origin === SearchOrigin.LOAD_NEXT) {
      // Check if we already have enough data
      const start = this.currentPage() * this.facade.numResultsDisplayed();
      const end = start + this.facade.numResultsDisplayed();

      if (end <= this.allCalls().length) {
        this.currentPage.set(this.currentPage() + 1);
        this.outboundCalls.set(this.allCalls().slice(start, end));
        this.loading.set(false);
        this.hasMore.set(this.allCalls().length > end);
        return;
      }

      criteria.pagination.cursor = this.allCalls().at(-1)?.id ?? null;
      this.currentPage.set(this.currentPage() + 1);
    } else if (origin === SearchOrigin.LOAD_PREV) {
      if (this.currentPage() > 1) this.currentPage.set(this.currentPage() - 1);

      const start = (this.currentPage() - 1) * this.facade.numResultsDisplayed();
      const end = start + this.facade.numResultsDisplayed();
      this.outboundCalls.set(this.allCalls().slice(start, end));
      this.hasMore.set(true);
      this.loading.set(false);
      return;
    } else if (origin === SearchOrigin.RESET_OR_RELOAD || origin === SearchOrigin.FILTER_CHANGE) {
      this.currentPage.set(1);
      this.allCalls.set([]);
    }

    this.outboundCallService.getOutboundCalls(criteria).subscribe({
      next: (calls) => {
        const merged =
          origin === SearchOrigin.LOAD_NEXT
            ? [...this.allCalls(), ...calls.results]
            : calls.results;

        this.totalResults.set(calls.totalResults);
        this.allCalls.set(merged);

        const start = (this.currentPage() - 1) * this.facade.numResultsDisplayed();
        const end = start + this.facade.numResultsDisplayed();

        this.outboundCalls.set(merged.slice(start, end));

        this.hasMore.set(calls.results.length === this.facade.numResultsDisplayed());
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading outbound calls:', err);
        this.loading.set(false);
      },
    });
  }

  resetCalls() {
    this.currentPage.set(1);
    this.cursor.set(null);
    this.hasMore.set(true);

    this.loadCall(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
  }

  setPageSize(size: number) {
    this.facade.setPageSize(size);
    this.currentPage.set(1);
    this.loadCall(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
  }

  copyExternalId(externalId: string) {
    if (!externalId) {
      return;
    }
    window.navigator.clipboard.writeText(externalId);
    this.toast.showInfo('Copiado', `ID ${externalId} copiada al portapapeles`);
  }

  onTranscriptionClick(call: any) {
    if (call.transcription && call.transcription.length > 0) {
      this.selectedTranscription = call.transcription;
      this.selectedCustomerName = call.customer?.name ?? 'Cliente';
      this.isTranscriptionDialogVisible = true;
    } else {
      console.warn('No transcription available for this call');
    }
  }

  openAudioDialog(call: OutboundCall) {
    this.isPlaying = false;
    this.playingCallId.set(null);
    this.selectedCallAudio = call;
    this.isAudioDialogVisible = true;
    this.onTranscriptionClick(call);
  }

  playCallAudio(call: OutboundCall) {
    if (this.playingCallId() === call.id && this.waveSurfer?.isPlaying()) {
      this.waveSurfer?.pause();
      this.playingCallId.set(null);
      return;
    }

    this.loadingAudioCalls[call.id] = true; // mark loading

    this.callAudioService.downloadAudio(call.id).subscribe({
      next: (blob) => {
        if (this.isAudioUnavailableBlob(blob)) {
          this.loadingAudioCalls[call.id] = false;
          this.playingCallId.set(null);
          this.toast.showError('Error', 'El audio no está disponible');
          return;
        }

        const audioUrl = URL.createObjectURL(blob);

        // Destroy old WaveSurfer if exists
        if (this.waveSurfer) this.waveSurfer.destroy();

        // Create hidden WaveSurfer instance
        this.waveSurfer = WaveSurfer.create({
          container: document.createElement('div'),
          waveColor: '#5146e1',
          progressColor: '#92a1ad',
          cursorColor: '#11005b',
          height: 50,
        });

        this.waveSurfer.load(audioUrl);

        this.waveSurfer.on('ready', () => {
          this.waveSurfer?.play();
          this.playingCallId.set(call.id);
          this.loadingAudioCalls[call.id] = false;
        });

        this.waveSurfer.on('finish', () => {
          this.playingCallId.set(null);
          this.currentTimeMap[call.id] = 0;
        });

        this.waveSurfer.on('audioprocess', () => {
          if (!this.waveSurfer) return;
          this.currentTimeMap[call.id] = this.waveSurfer.getCurrentTime();
        });

        this.waveSurfer.on('ready', () => {
          if (this.waveSurfer) {
            this.durationMap[call.id] = this.waveSurfer.getDuration();
          }
        });
      },
      error: () => {
        this.loadingAudioCalls[call.id] = false;
        this.playingCallId.set(null);
        this.toast.showError('Error', 'No se pudo descargar el audio');
      },
    });
  }

  isPlaying = false;

  downloadCallAudio(call: OutboundCall) {
    this.loadingDownloads[call.id] = true;

    this.callAudioService.downloadAudio(call.id).subscribe({
      next: (blob) => {
        if (this.isAudioUnavailableBlob(blob)) {
          this.loadingDownloads[call.id] = false;
          this.toast.showError('Error', 'El audio no está disponible');
          return;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `call_${call.id}.wav`;
        a.click();
        URL.revokeObjectURL(url);
        this.loadingDownloads[call.id] = false;
      },
      error: () => {
        this.loadingDownloads[call.id] = false;
        this.toast.showError('Error', 'No se pudo descargar el audio');
      },
    });
  }

  private isAudioUnavailableBlob(blob: Blob | null | undefined): boolean {
    return !blob || blob.size === 0;
  }

  formatTime(seconds: number = 0): string {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${secs}`;
  }

  onAudioDialogHide() {
    if (this.waveSurfer) {
      this.waveSurfer.destroy();
      this.waveSurfer = null;
    }

    this.playingCallId.set(null);
    this.isPlaying = false;
    this.selectedCallAudio = null;
  }
}
